import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { opsApi, apiError, fmtINR, GRAD, FONT_HEAD } from './opsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Store, Plus, Package, ImagePlus, Trash2, Pencil } from 'lucide-react';
import { confirmDialog } from '../../components/ConfirmDialog';

const EMPTY_FORM = { name: '', colors: '', price: '', total_count: '', description: '' };

export default function OpsShopHall() {
  const [items, setItems] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // product being edited, or null
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    opsApi.get('/shop/products', { params: { include_inactive: true, page_size: 100 } })
      .then((r) => setItems(r.data.items))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setShowAdd(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '', colors: (p.colors || []).join(', '),
      price: p.price ?? '', total_count: p.total_count ?? '', description: p.description || '',
    });
    setImageFile(null);
    setImagePreview(p.image || null);
    setShowAdd(true);
  };

  const onPickImage = (file) => {
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Product name is required'); return; }
    if (!form.price || Number(form.price) <= 0) { toast.error('Enter a valid price'); return; }
    if (form.total_count === '' || Number(form.total_count) < 0) { toast.error('Enter total product count'); return; }

    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('colors', form.colors);
    fd.append('price', form.price);
    fd.append('total_count', form.total_count);
    fd.append('description', form.description);
    if (imageFile) fd.append('image', imageFile);

    setBusy(true);
    try {
      if (editing) {
        await opsApi.put(`/shop/products/${editing.id}`, fd);
        toast.success('Product updated');
      } else {
        await opsApi.post('/shop/products', fd);
        toast.success('Product added to Shop Hall');
      }
      setShowAdd(false);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleActive = async (p) => {
    try {
      if (p.is_active) {
        if (!(await confirmDialog({ title: 'Remove from Shop Hall', message: 'Remove this product from the Shop Hall? You can add it back later.', danger: true, confirmLabel: 'Remove' }))) return;
        await opsApi.delete(`/shop/products/${p.id}`);
        toast.success('Removed from Shop Hall');
      } else {
        const fd = new FormData();
        fd.append('is_active', 'true');
        await opsApi.put(`/shop/products/${p.id}`, fd);
        toast.success('Product re-listed');
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="ops-shop-hall-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>Shop Hall</h1>
          <p className="text-sm text-slate-500 mt-1">Manage the device catalog every institution sees in their Devices → Shop tab</p>
        </div>
        <Button data-testid="add-shop-product-btn" onClick={openAdd} className="text-white rounded-xl h-10 border-0" style={{ background: GRAD }}>
          <Plus className="w-4 h-4 mr-1.5" /> Add product
        </Button>
      </div>

      {!items ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#34A853]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 text-center py-16 text-slate-400">
          <Store className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No products yet. Add your first device to the Shop Hall catalog.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`shop-product-${p.id}`}
              className={`bg-white rounded-2xl border overflow-hidden ${p.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
              <div className="h-32 bg-slate-50 flex items-center justify-center">
                {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-10 h-10 text-slate-300" />}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-900" style={FONT_HEAD}>{p.name}</h3>
                  {!p.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium shrink-0">Hidden</span>}
                </div>
                {p.colors?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.colors.map((c) => <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{c}</span>)}
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-base font-bold text-slate-900 tabular-nums">{fmtINR(p.price)}</span>
                  <span className="text-xs text-slate-400" data-testid={`shop-product-count-${p.id}`}>Stock: {p.total_count}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="h-8 text-xs rounded-lg flex-1" onClick={() => openEdit(p)}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs rounded-lg flex-1 text-red-600 border-red-200" onClick={() => toggleActive(p)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> {p.is_active ? 'Remove' : 'Re-list'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>{editing ? 'Edit product' : 'Add product'}</DialogTitle>
            <DialogDescription>This reflects immediately in every institution's Shop tab.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Device name *</Label>
              <Input data-testid="shop-product-name-input" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Available colors (comma-separated)</Label>
              <Input data-testid="shop-product-colors-input" placeholder="e.g. Black, White, Blue" value={form.colors}
                onChange={(e) => setForm((f) => ({ ...f, colors: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Picture</Label>
              <label className="mt-1 flex items-center gap-3 border border-dashed border-slate-300 rounded-xl p-3 cursor-pointer hover:border-slate-400">
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" className="w-14 h-14 rounded-lg object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-slate-50 flex items-center justify-center"><ImagePlus className="w-5 h-5 text-slate-300" /></div>
                )}
                <span className="text-xs text-slate-500">Click to upload an image from your computer (max 3MB)</span>
                <input type="file" accept="image/*" className="hidden" data-testid="shop-product-image-input"
                  onChange={(e) => onPickImage(e.target.files?.[0])} />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Price (₹) *</Label>
                <Input data-testid="shop-product-price-input" type="number" min="1" value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Total product count *</Label>
                <Input data-testid="shop-product-count-input" type="number" min="0" value={form.total_count}
                  onChange={(e) => setForm((f) => ({ ...f, total_count: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea data-testid="shop-product-description-input" rows={3} value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" />
            </div>
            <Button data-testid="shop-product-submit" disabled={busy} onClick={submit}
              className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add product'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
